import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import apiService from "../services/apiService";
import I from "../component/Icon";
// ======================================================
// PAYMENT API
// ======================================================
const RAZORPAY_ORDER_API =
  "Payment/createRazorpayOrder";
const RAZORPAY_VERIFY_API =
  "Payment/verifyRazorpayPayment";
const RAZORPAY_KEY_ID =
  "rzp_live_TkB8Olj4FbJVxc";
// ======================================================
// NOTIFICATION
// ======================================================
const showNotification = (
  type,
  message
) => {
  if (
    typeof document === "undefined" ||
    typeof window === "undefined"
  ) {
    return;
  }
  const oldNotification =
    document.getElementById(
      "my-exam-notification"
    );
  if (oldNotification) {
    oldNotification.remove();
  }
  const notification =
    document.createElement("div");
  notification.id =
    "my-exam-notification";
  notification.textContent =
    `${type === "success"
      ? "✓"
      : "✕"
    } ${message}`;
  Object.assign(
    notification.style,
    {
      position: "fixed",
      top: "20px",
      right: "20px",
      zIndex: "9999999",
      minWidth: "280px",
      maxWidth: "420px",
      padding: "12px 16px",
      borderRadius: "8px",
      backgroundColor:
        type === "success"
          ? "#d1e7dd"
          : "#f8d7da",
      color:
        type === "success"
          ? "#0f5132"
          : "#842029",
      border:
        type === "success"
          ? "1px solid #badbcc"
          : "1px solid #f5c2c7",
      fontSize: "14px",
      fontWeight: "600",
      boxShadow:
        "0 4px 12px rgba(0,0,0,0.15)",
    }
  );
  document.body.appendChild(
    notification
  );
  window.setTimeout(() => {
    if (
      notification.parentNode
    ) {
      notification.remove();
    }
  }, 3000);
};
// ======================================================
// API ERROR
// ======================================================
const getApiErrorMessage = (
  error,
  fallback
) => {
  const data =
    error?.response?.data;
  if (
    typeof data === "string" &&
    data.trim()
  ) {
    return data;
  }
  return (
    data?.failureReason ||
    data?.FailureReason ||
    data?.message ||
    data?.Message ||
    error?.message ||
    fallback
  );
};
// ======================================================
// JSON PARSE
// ======================================================
const parseMaybeJson = (
  value
) => {
  if (
    typeof value !== "string"
  ) {
    return value;
  }
  try {
    return JSON.parse(
      value
    );
  } catch {
    return value;
  }
};
// ======================================================
// MAIN PAGE
// ======================================================
// Compare complete API rows so distinct registrations/payment records stay separate.
const canonicalExamRow = (value) => {
  if (Array.isArray(value)) return value.map(canonicalExamRow);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonicalExamRow(value[key])]));
  }
  return value;
};
const uniqueExamRows = (rows) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = JSON.stringify(canonicalExamRow(row));
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
// One visible exam per student; keep every book-level row for detail/payment use.
const groupStudentExamRows = (rows) => {
  const grouped = new Map();
  rows.forEach((row, index) => {
    const examId = row.examId ?? row.ExamId ?? row.examID ?? row.ExamID;
    // Without an ExamId, do not guess identity from the exam name.
    const key = examId != null && String(examId) !== "0" && String(examId) !== ""
      ? JSON.stringify([row.studentId ?? row.StudentId ?? "", String(examId)])
      : `unresolved-${index}`;
    const existing = grouped.get(key);
    if (existing) existing.bookDetails.push(row);
    else grouped.set(key, { ...row, bookDetails: [row] });
  });
  return Array.from(grouped.values());
};
export default function MyExamPage() {
  // ====================================================
  // STATES
  // ====================================================
  const [exams, setExams] =
    useState([]);
  const [loading, setLoading] =
    useState(false);
  const [search, setSearch] =
    useState("");
  const [
    viewExam,
    setViewExam,
  ] = useState(null);
  const [
    payExam,
    setPayExam,
  ] = useState(null);
  const [
    paymentLoading,
    setPaymentLoading,
  ] = useState(false);
  // ====================================================
  // PAGINATION
  // ====================================================
  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);
  const pageSize = 10;
  // ====================================================
  // CURRENT STUDENT
  // ====================================================
  let storedUser = {};
  try {
    storedUser =
      JSON.parse(
        sessionStorage.getItem(
          "user"
        ) || "{}"
      );
  } catch {
    storedUser = {};
  }
  const studentId =
    Number(
      sessionStorage.getItem(
        "studentId"
      ) ??
      storedUser?.studentId ??
      storedUser?.StudentId ??
      0
    );
  // ====================================================
  // FORMAT DATE
  // ====================================================
  const formatDate = (
    date
  ) => {
    if (!date) {
      return "-";
    }
    try {
      return new Date(
        date
      ).toLocaleDateString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    } catch {
      return date;
    }
  };
  // ====================================================
  // FORMAT FEE
  // ====================================================
  const formatFee = (
    amount
  ) => {
    return new Intl.NumberFormat(
      "en-IN",
      {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }
    ).format(
      Number(
        amount || 0
      )
    );
  };
  // ====================================================
  // PAYMENT STATUS
  //
  // Backend:
  // UnPaid -> Pending
  // Paid   -> Paid
  // ====================================================
  const getPaymentStatus = (
    item
  ) => {
    const status =
      String(
        item?.paymentStatus ??
        item?.PaymentStatus ??
        ""
      )
        .trim()
        .toLowerCase();
    if (
      status === "paid"
    ) {
      return "Paid";
    }
    return "Pending";
  };
  // ====================================================
  // FETCH MY EXAMS
  // ====================================================
  const fetchMyExams =
    async () => {
      if (!studentId) {
        setExams([]);
        showNotification(
          "error",
          "Student Id not found."
        );
        return;
      }
      try {
        setLoading(true);
        const response =
          await apiService.get(
            `Exam/getStudentIdWiseExamList?Id=${encodeURIComponent(
              studentId
            )}`
          );
        const data =
          parseMaybeJson(
            response?.data
          );
        console.log(
          "My Exam API Response:",
          data
        );
        if (
          data?.isSuccess === false ||
          data?.IsSuccess === false
        ) {
          throw new Error(
            data?.failureReason ??
            data?.FailureReason ??
            data?.message ??
            data?.Message ??
            "Unable to load exams."
          );
        }
        const output =
          parseMaybeJson(
            data?.output ??
            data?.Output ??
            data?.data ??
            data?.Data ??
            data
          );
        const list =
          Array.isArray(output)
            ? output
            : Array.isArray(
              output?.result
            )
              ? output.result
              : Array.isArray(
                output?.Result
              )
                ? output.Result
                : Array.isArray(
                  output?.data
                )
                  ? output.data
                  : Array.isArray(
                    output?.Data
                  )
                    ? output.Data
                    : [];
        // ==============================================
        // MAP PAYLOAD
        // ==============================================
        const uniqueList = uniqueExamRows(list);
        if (uniqueList.length !== list.length) {
          console.warn("Duplicate student exam API rows removed:", list.length - uniqueList.length);
        }
        const mapped =
          groupStudentExamRows(uniqueList).map(
            (
              item,
              index
            ) => {
              return {
                ...item,
                // INTERNAL ONLY
                rowId:
                  `${item?.studentDetailId ?? index}-${item?.examName ?? "exam"}-${index}`,
                // INTERNAL ONLY - PAYMENT
                schoolId:
                  item?.schoolId ??
                  item?.SchoolId ??
                  null,
                studentDetailId:
                  Number(
                    item?.studentDetailId ??
                    item?.StudentDetailId ??
                    0
                  ),
                studentId:
                  Number(
                    item?.studentId ??
                    item?.StudentId ??
                    0
                  ),
                // VISIBLE
                schoolName:
                  item?.schoolName ??
                  item?.SchoolName ??
                  "-",
                studentFullName:
                  item?.studentFullName ??
                  item?.StudentFullName ??
                  "-",
                standardName:
                  item?.standardName ??
                  item?.StandardName ??
                  "-",
                examName:
                  item?.examName ??
                  item?.ExamName ??
                  "-",
                startTime:
                  item?.startTime ??
                  item?.StartTime ??
                  "-",
                endTime:
                  item?.endTime ??
                  item?.EndTime ??
                  "-",
                examDate:
                  item?.examDate ??
                  item?.ExamDate ??
                  null,
                resultDate:
                  item?.resultDate ??
                  item?.ResultDate ??
                  null,
                resultLink:
                  item?.resultLink ??
                  item?.ResultLink ??
                  "",
                examStatus:
                  item?.examStatus ??
                  item?.ExamStatus ??
                  "-",
                examTotalFees:
                  Number(
                    item?.examTotalFees ??
                    item?.ExamTotalFees ??
                    0
                  ),
                // INTERNAL
                feesPaid:
                  Number(
                    item?.feesPaid ??
                    item?.FeesPaid ??
                    0
                  ),
                pendingFees:
                  Number(
                    item?.pendingFees ??
                    item?.PendingFees ??
                    0
                  ),
                paymentStatus:
                  getPaymentStatus(
                    item
                  ),
              };
            }
          );
        // Result Date/Link fallback: the student-wise API may omit ExamId and result fields.
        // Resolve using the main Exams list first (unique name + exam date match only),
        // then use getExamById for any remaining missing result information.
        // Registration/payment data is left untouched.
        const needsResult = (exam) => !exam.resultDate || !String(exam.resultLink ?? "").trim();
        const unwrapList = (value) => {
          const root = parseMaybeJson(value);
          const output = parseMaybeJson(root?.output ?? root?.Output ?? root?.data ?? root?.Data ?? root);
          const rows = Array.isArray(output) ? output
            : parseMaybeJson(output?.result ?? output?.Result ?? output?.data ?? output?.Data ?? []);
          return { rows: Array.isArray(rows) ? rows : [],
            total: Number(output?.rowCount ?? output?.RowCount ?? output?.totalRecords ?? output?.TotalRecords ?? 0) };
        };
        const normalizedExamName = (value) => String(value ?? "").trim().replace(/\s+/g, " ").toLocaleLowerCase();
        const normalizedExamDate = (value) => {
          if (!value) return "";
          const raw = String(value).trim();
          const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
          if (match) return match[1];
          const dt = new Date(raw);
          return Number.isNaN(dt.getTime()) ? "" : `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
        };
        let fullExamList = [];
        if (mapped.some(needsResult)) {
          try {
            const request = { page: 1, pageSize: 100, searchTerm: "", sortDirection: "DESC", sortField: "Id" };
            const first = await apiService.post("Exam/getPaginatedExam", request);
            const firstData = unwrapList(first?.data);
            fullExamList = [...firstData.rows];
            const totalPages = Math.ceil(Math.max(firstData.total, fullExamList.length) / request.pageSize);
            for (let page = 2; page <= totalPages; page += 1) {
              const next = await apiService.post("Exam/getPaginatedExam", {...request, page});
              const nextRows = unwrapList(next?.data).rows;
              if (!nextRows.length) break;
              fullExamList.push(...nextRows);
            }
          } catch (error) {
            console.warn("Unable to load master exams for result fallback", error);
          }
        }
        const enriched = await Promise.all(mapped.map(async (exam) => {
          if (!needsResult(exam)) return exam;
          const suppliedExamId = Number(exam?.examId ?? exam?.ExamId ?? exam?.examID ?? exam?.ExamID ?? 0);
          const candidates = fullExamList.filter((master) => {
            const masterId = Number(master?.id ?? master?.Id ?? master?.examId ?? master?.ExamId ?? 0);
            if (suppliedExamId) return masterId === suppliedExamId;
            return normalizedExamName(master?.examName ?? master?.ExamName ?? master?.name ?? master?.Name) === normalizedExamName(exam.examName)
              && normalizedExamDate(master?.examDate ?? master?.ExamDate) === normalizedExamDate(exam.examDate)
              && !!normalizedExamDate(exam.examDate);
          });
          // Never attach another exam's result when names/dates are ambiguous.
          const master = candidates.length === 1 ? candidates[0] : null;
          if (!master && candidates.length > 1) console.warn("Ambiguous exam result match", exam.examName, exam.examDate);
          const matchedExamId = suppliedExamId || Number(master?.id ?? master?.Id ?? master?.examId ?? master?.ExamId ?? 0);
          let resolved = {
            ...exam,
            resultDate: exam.resultDate || master?.resultDate || master?.ResultDate || null,
            resultLink: exam.resultLink || master?.resultLink || master?.ResultLink || "",
          };
          if (matchedExamId && needsResult(resolved)) {
            try {
              const detailResponse = await apiService.get(`Exam/getExamById/${matchedExamId}`);
              const root = parseMaybeJson(detailResponse?.data);
              if (root?.isSuccess !== false && root?.IsSuccess !== false) {
                let detail = parseMaybeJson(root?.output ?? root?.Output ?? root?.data ?? root?.Data ?? root);
                if (Array.isArray(detail)) detail = detail[0];
                else if (Array.isArray(detail?.result)) detail = detail.result[0];
                else if (Array.isArray(detail?.Result)) detail = detail.Result[0];
                else detail = detail?.result ?? detail?.Result ?? detail;
                resolved = {
                  ...resolved,
                  resultDate: resolved.resultDate || detail?.resultDate || detail?.ResultDate || null,
                  resultLink: resolved.resultLink || detail?.resultLink || detail?.ResultLink || "",
                };
              }
            } catch (error) {
              console.warn(`Result details not available for ExamId ${matchedExamId}`, error);
            }
          }
          if (needsResult(resolved)) console.info("Result field missing from available API responses:", { examName: exam.examName, examDate: exam.examDate, matchedExamId, resultDate: resolved.resultDate, resultLink: resolved.resultLink });
          return resolved;
        }));
        setExams(
          enriched
        );
      } catch (error) {
        console.error(
          "My Exam API Error:",
          error
        );
        setExams([]);
        showNotification(
          "error",
          getApiErrorMessage(
            error,
            "Unable to load exams."
          )
        );
      } finally {
        setLoading(false);
      }
    };
  // ====================================================
  // LOAD
  // ====================================================
  useEffect(() => {
    fetchMyExams();
  }, [studentId]);
  // ====================================================
  // SEARCH
  // ====================================================
  const filteredExams =
    useMemo(() => {
      const searchText =
        search
          .trim()
          .toLowerCase();
      if (!searchText) {
        return exams;
      }
      return exams.filter(
        (exam) => {
          const formattedDate =
            exam.examDate
              ? formatDate(
                exam.examDate
              )
              : "";
          const allText = [
            exam.schoolName,
            exam.studentFullName,
            exam.standardName,
            exam.examName,
            exam.startTime,
            exam.endTime,
            exam.examDate,
            formattedDate,
            exam.resultDate,
            exam.resultLink,
            exam.examStatus,
            exam.examTotalFees,
            exam.pendingFees,
            exam.paymentStatus,
          ]
            .join(" ")
            .toLowerCase();
          return allText.includes(
            searchText
          );
        }
      );
    }, [
      exams,
      search,
    ]);
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);
  // ====================================================
  // PAGINATION
  // ====================================================
  const totalRecords =
    filteredExams.length;
  const totalPages =
    Math.max(
      1,
      Math.ceil(
        totalRecords /
        pageSize
      )
    );
  useEffect(() => {
    if (
      currentPage >
      totalPages
    ) {
      setCurrentPage(
        totalPages
      );
    }
  }, [
    currentPage,
    totalPages,
  ]);
  const startIndex =
    (currentPage - 1) *
    pageSize;
  const endIndex =
    startIndex +
    pageSize;
  const pageRecords =
    filteredExams.slice(
      startIndex,
      endIndex
    );
  const pageNumbers =
    Array.from(
      {
        length:
          totalPages,
      },
      (_, index) =>
        index + 1
    );
  // ====================================================
  // VIEW
  // ====================================================
  const openView = (
    exam
  ) => {
    setViewExam(
      exam
    );
  };
  const closeView = () => {
    setViewExam(
      null
    );
  };
  // ====================================================
  // PAYMENT POPUP
  // ====================================================
  const openPaymentPopup =
    (exam) => {
      if (
        exam.paymentStatus ===
        "Paid"
      ) {
        return;
      }
      if (
        Number(
          exam.pendingFees
        ) <= 0
      ) {
        showNotification(
          "error",
          "No pending fees available."
        );
        return;
      }
      if (
        !Number(
          exam.studentDetailId
        )
      ) {
        showNotification(
          "error",
          "Student Detail Id not found."
        );
        return;
      }
      setPayExam(
        exam
      );
    };
  const closePaymentPopup =
    () => {
      if (
        paymentLoading
      ) {
        return;
      }
      setPayExam(
        null
      );
    };
  // ====================================================
  // LOAD RAZORPAY
  // ====================================================
  const loadRazorpayScript =
    () => {
      return new Promise(
        (resolve) => {
          if (
            typeof window ===
            "undefined"
          ) {
            resolve(false);
            return;
          }
          if (
            window.Razorpay
          ) {
            resolve(true);
            return;
          }
          const existing =
            document.querySelector(
              'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
            );
          if (existing) {
            existing.addEventListener(
              "load",
              () =>
                resolve(true)
            );
            existing.addEventListener(
              "error",
              () =>
                resolve(false)
            );
            return;
          }
          const script =
            document.createElement(
              "script"
            );
          script.src =
            "https://checkout.razorpay.com/v1/checkout.js";
          script.async =
            true;
          script.onload =
            () =>
              resolve(true);
          script.onerror =
            () =>
              resolve(false);
          document.body.appendChild(
            script
          );
        }
      );
    };
  // ====================================================
  // PAYMENT
  // ====================================================
  const handlePayment =
    async () => {
      if (!payExam) {
        return;
      }
      const studentDetailId =
        Number(
          payExam?.studentDetailId
        );
      const paymentStudentId =
        Number(
          payExam?.studentId ??
          studentId
        );
      if (
        !paymentStudentId
      ) {
        showNotification(
          "error",
          "Student Id not found."
        );
        return;
      }
      if (
        !studentDetailId
      ) {
        showNotification(
          "error",
          "Student Detail Id not found."
        );
        return;
      }
      try {
        setPaymentLoading(
          true
        );
        // ==============================================
        // CREATE ORDER
        // ==============================================
        const orderResponse =
          await apiService.post(
            RAZORPAY_ORDER_API,
            {
              studentId:
                paymentStudentId,
              studentDetailIds: [
                studentDetailId,
              ],
            }
          );
        const orderRoot =
          parseMaybeJson(
            orderResponse?.data
          );
        if (
          orderRoot?.isSuccess ===
          false ||
          orderRoot?.IsSuccess ===
          false
        ) {
          throw new Error(
            orderRoot?.failureReason ??
            orderRoot?.FailureReason ??
            orderRoot?.message ??
            orderRoot?.Message ??
            "Unable to create payment order."
          );
        }
        const order =
          parseMaybeJson(
            orderRoot?.output ??
            orderRoot?.Output ??
            orderRoot?.data ??
            orderRoot?.Data ??
            orderRoot
          );
        const orderId =
          order?.orderId ??
          order?.OrderId ??
          order?.id ??
          order?.Id;
        const amount =
          Number(
            order?.amount ??
            order?.Amount ??
            payExam.pendingFees ??
            0
          );
        const currency =
          order?.currency ??
          order?.Currency ??
          "INR";
        if (!orderId) {
          throw new Error(
            "Razorpay Order Id not found."
          );
        }
        if (
          !Number.isFinite(
            amount
          ) ||
          amount <= 0
        ) {
          throw new Error(
            "Invalid payment amount."
          );
        }
        const loaded =
          await loadRazorpayScript();
        if (
          !loaded ||
          !window.Razorpay
        ) {
          throw new Error(
            "Razorpay Checkout could not load."
          );
        }
        // ==============================================
        // RAZORPAY OPTIONS
        // ==============================================
        const options = {
          key:
            RAZORPAY_KEY_ID,
          amount:
            Math.round(
              amount * 100
            ),
          currency,
          name:
            "GoreExam",
          description:
            `${payExam.examName} Exam Fee`,
          order_id:
            orderId,
          prefill: {
            name:
              payExam.studentFullName ??
              storedUser?.name ??
              storedUser?.Name ??
              "",
            email:
              storedUser?.email ??
              storedUser?.Email ??
              sessionStorage.getItem(
                "email"
              ) ??
              "",
            contact:
              storedUser?.mobileNo ??
              storedUser?.MobileNo ??
              storedUser?.phone ??
              storedUser?.Phone ??
              "",
          },
          notes: {
            studentId:
              String(
                paymentStudentId
              ),
            studentDetailId:
              String(
                studentDetailId
              ),
            examName:
              payExam.examName ??
              "",
          },
          handler:
            async function (
              response
            ) {
              try {
                setPaymentLoading(
                  true
                );
                const verifyResponse =
                  await apiService.post(
                    RAZORPAY_VERIFY_API,
                    {
                      orderId:
                        response
                          .razorpay_order_id,
                      razorpayPaymentId:
                        response
                          .razorpay_payment_id,
                      razorpaySignature:
                        response
                          .razorpay_signature,
                    }
                  );
                const verifyData =
                  parseMaybeJson(
                    verifyResponse?.data
                  );
                if (
                  verifyData?.isSuccess ===
                  false ||
                  verifyData?.IsSuccess ===
                  false
                ) {
                  throw new Error(
                    verifyData?.failureReason ??
                    verifyData?.FailureReason ??
                    verifyData?.message ??
                    verifyData?.Message ??
                    "Payment verification failed."
                  );
                }
                showNotification(
                  "success",
                  "Payment completed successfully."
                );
                setPayExam(
                  null
                );
                await fetchMyExams();
                setViewExam(
                  null
                );
              } catch (error) {
                console.error(
                  "Payment Verification Error:",
                  error
                );
                showNotification(
                  "error",
                  getApiErrorMessage(
                    error,
                    "Payment verification failed."
                  )
                );
              } finally {
                setPaymentLoading(
                  false
                );
              }
            },
          modal: {
            ondismiss:
              function () {
                setPaymentLoading(
                  false
                );
              },
          },
          theme: {
            color:
              "#2563eb",
          },
        };
        const razorpay =
          new window.Razorpay(
            options
          );
        razorpay.on(
          "payment.failed",
          function (
            response
          ) {
            showNotification(
              "error",
              response?.error
                ?.description ||
              "Payment failed."
            );
            setPaymentLoading(
              false
            );
          }
        );
        razorpay.open();
      } catch (error) {
        console.error(
          "Payment Error:",
          error
        );
        showNotification(
          "error",
          getApiErrorMessage(
            error,
            "Unable to start payment."
          )
        );
        setPaymentLoading(
          false
        );
      }
    };
  // ====================================================
  // VIEW PAGE
  // ====================================================
  if (viewExam) {
    const details = [
      {
        label:
          "School Name",
        value:
          viewExam.schoolName,
      },
      {
        label:
          "Student Name",
        value:
          viewExam.studentFullName,
      },
      {
        label:
          "Standard",
        value:
          viewExam.standardName,
      },
      {
        label:
          "Exam Name",
        value:
          viewExam.examName,
      },
      {
        label:
          "Exam Date",
        value:
          formatDate(
            viewExam.examDate
          ),
      },
      {
        label:
          "Result Date",
        value:
          formatDate(
            viewExam.resultDate
          ),
      },
      {
        label:
          "Result Link",
        value:
          viewExam.resultLink ? (
            <button
              type="button"
              className="btn btn-p btn-sm"
              onClick={() => {
                const link = String(
                  viewExam.resultLink
                ).trim();
                const finalLink =
                  /^https?:\/\//i.test(link)
                    ? link
                    : `https://${link}`;
                window.open(
                  finalLink,
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            >
              View Result
            </button>
          ) : (
            "-"
          ),
      },
      {
        label:
          "Start Time",
        value:
          viewExam.startTime,
      },
      {
        label:
          "End Time",
        value:
          viewExam.endTime,
      },
      {
        label:
          "Exam Status",
        value:
          viewExam.examStatus,
      },
      {
        label:
          "Exam Total Fees",
        value:
          formatFee(
            viewExam.examTotalFees
          ),
      },
      {
        label:
          "Pending Fees",
        value:
          formatFee(
            viewExam.pendingFees
          ),
      },
      {
        label:
          "Payment Status",
        value:
          viewExam.paymentStatus,
      },
    ];
    return (
      <>
        {/* HEADER */}
        <div
          className="ph"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              "space-between",
            gap: 12,
          }}
        >
          <div>
            <div
              className="ph-t"
            >
              Exam Details
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color:
                  "var(--t3)",
              }}
            >
              {
                viewExam.examName
              }
            </div>
          </div>
          <button
            type="button"
            className="btn btn-g"
            onClick={
              closeView
            }
          >
            ← Back
          </button>
        </div>
        {/* VIEW CARD */}
        <div className="card">
          <div
            style={{
              padding: 20,
            }}
          >
            {/* TITLE */}
            <div
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems:
                  "center",
                flexWrap: "wrap",
                gap: 12,
                paddingBottom: 18,
                marginBottom: 18,
                borderBottom:
                  "1px solid var(--bd)",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color:
                      "var(--t1)",
                  }}
                >
                  {
                    viewExam.examName
                  }
                </div>
                <div
                  style={{
                    fontSize: 12,
                    marginTop: 5,
                    color:
                      "var(--t3)",
                  }}
                >
                  {
                    viewExam.schoolName
                  }
                </div>
              </div>
              {/* PAYMENT STATUS */}
              {viewExam
                .paymentStatus ===
                "Paid" ? (
                <span
                  style={{
                    padding:
                      "7px 15px",
                    borderRadius: 20,
                    background:
                      "#dcfce7",
                    color:
                      "#166534",
                    border:
                      "1px solid #86efac",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Paid
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    openPaymentPopup(
                      viewExam
                    )
                  }
                  style={{
                    padding:
                      "7px 15px",
                    borderRadius: 20,
                    background:
                      "#fef3c7",
                    color:
                      "#92400e",
                    border:
                      "1px solid #f59e0b",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor:
                      "pointer",
                  }}
                >
                  Pending
                </button>
              )}
            </div>
            {/* DETAILS */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 16,
              }}
            >
              {details.map(
                (
                  detail,
                  index
                ) => (
                  <div
                    key={
                      index
                    }
                    style={{
                      border:
                        "1px solid var(--bd)",
                      borderRadius: 8,
                      padding: 14,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform:
                          "uppercase",
                        color:
                          "var(--t3)",
                        marginBottom: 6,
                      }}
                    >
                      {
                        detail.label
                      }
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color:
                          "var(--t1)",
                      }}
                    >
                      {
                        detail.value ??
                        "-"
                      }
                    </div>
                  </div>
                )
              )}
            </div>
            <div
              style={{
                marginTop: 20,
                border:
                  "1px solid var(--bd)",
                borderRadius: 8,
                padding: 16,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  marginBottom: 15,
                }}
              >
                Payment Details
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(170px, 1fr))",
                  gap: 14,
                }}
              >
                {/* TOTAL */}
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color:
                        "var(--t3)",
                    }}
                  >
                    Total Fees
                  </div>
                  <strong
                    style={{
                      display: "block",
                      marginTop: 4,
                      fontSize: 17,
                    }}
                  >
                    {formatFee(
                      viewExam
                        .examTotalFees
                    )}
                  </strong>
                </div>
                {/* PENDING */}
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color:
                        "var(--t3)",
                    }}
                  >
                    Pending Fees
                  </div>
                  <strong
                    style={{
                      display: "block",
                      marginTop: 4,
                      fontSize: 17,
                      color:
                        Number(
                          viewExam
                            .pendingFees
                        ) > 0
                          ? "#b45309"
                          : "#166534",
                    }}
                  >
                    {formatFee(
                      viewExam
                        .pendingFees
                    )}
                  </strong>
                </div>
                {/* STATUS */}
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color:
                        "var(--t3)",
                    }}
                  >
                    Payment Status
                  </div>
                  <strong
                    style={{
                      display: "block",
                      marginTop: 4,
                      fontSize: 17,
                      color:
                        viewExam
                          .paymentStatus ===
                          "Paid"
                          ? "#166534"
                          : "#b45309",
                    }}
                  >
                    {
                      viewExam
                        .paymentStatus
                    }
                  </strong>
                </div>
              </div>
              {viewExam
                .paymentStatus !==
                "Paid" &&
                Number(
                  viewExam
                    .pendingFees
                ) > 0 && (
                  <div
                    style={{
                      marginTop: 18,
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-p"
                      onClick={() =>
                        openPaymentPopup(
                          viewExam
                        )
                      }
                    >
                      Pay{" "}
                      {formatFee(
                        viewExam
                          .pendingFees
                      )}
                    </button>
                  </div>
                )}
            </div>
          </div>
        </div>
        {payExam &&
          typeof document !==
          "undefined" &&
          createPortal(
            <PaymentPopup
              exam={payExam}
              loading={
                paymentLoading
              }
              onClose={
                closePaymentPopup
              }
              onPay={
                handlePayment
              }
              formatDate={
                formatDate
              }
              formatFee={
                formatFee
              }
            />,
            document.body
          )}
      </>
    );
  }
  // ====================================================
  // MAIN LIST
  // ====================================================
  return (
    <>
      <div className="ph">
        <div className="ph-t">
          My Exams
        </div>
      </div>
      {/* SEARCH */}
      <div
        className="toolbar"
        style={{
          marginBottom: 16,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 340,
          }}
        >
          <input
            type="text"
            className="fi"
            value={search}
            onChange={(e) =>
              setSearch(
                e.target.value
              )
            }
            placeholder="Search my exams..."
            style={{
              width: "100%",
              height: 40,
            }}
          />
        </div>
      </div>
      {/* TABLE */}
      <div className="card">
        <div
          className="tw"
          style={{
            overflowX: "auto",
          }}
        >
          <table
            style={{
              minWidth: 1380,
              width: "100%",
            }}
          >
            <thead>
              <tr>
                <th>
                  Exam Name
                </th>
                <th>
                  Start Time
                </th>
                <th>
                  End Time
                </th>
                <th>
                  Exam Date
                </th>
                <th>
                  Result Date
                </th>
                <th>
                  Result Link
                </th>
                <th>
                  Exam Status
                </th>
                <th>
                  Total Fees
                </th>
                <th>
                  Pending Fees
                </th>
                <th>
                  Payment Status
                </th>
                <th>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={12}
                    style={{
                      textAlign:
                        "center",
                      padding: 28,
                    }}
                  >
                    Loading exams...
                  </td>
                </tr>
              ) : pageRecords.length ===
                0 ? (
                <tr>
                  <td
                    colSpan={12}
                    style={{
                      textAlign:
                        "center",
                      padding: 28,
                    }}
                  >
                    No exams found
                  </td>
                </tr>
              ) : (
                pageRecords.map(
                  (exam) => (
                    <tr
                      key={
                        exam.rowId
                      }
                    >
                   <td className="tdb">
                        {
                          exam.examName
                        }
                      </td>
                      <td className="td2">
                        {
                          exam.startTime
                        }
                      </td>
                      <td className="td2">
                        {
                          exam.endTime
                        }
                      </td>
                      <td className="td2">
                        {formatDate(
                          exam.examDate
                        )}
                      </td>
                      <td className="td2">
                        {formatDate(
                          exam.resultDate
                        )}
                      </td>
                      <td>
                        {exam.resultLink ? (
                          <button
                            type="button"
                            className="btn btn-p btn-sm"
                            style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center" }}
                            onClick={() => {
                              const link = String(
                                exam.resultLink
                              ).trim();
                              const finalLink =
                                /^https?:\/\//i.test(link)
                                  ? link
                                  : `https://${link}`;
                              window.open(
                                finalLink,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            }}
                          >
                            View Result
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      {/* EXAM STATUS */}
                      <td>
                        <span
                          style={{
                            display:
                              "inline-block",
                            padding:
                              "5px 11px",
                            borderRadius: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            background:
                              String(
                                exam.examStatus
                              )
                                .toLowerCase() ===
                                "completed"
                                ? "#dcfce7"
                                : String(
                                  exam.examStatus
                                )
                                  .toLowerCase() ===
                                  "active"
                                  ? "#dbeafe"
                                  : "#fef3c7",
                            color:
                              String(
                                exam.examStatus
                              )
                                .toLowerCase() ===
                                "completed"
                                ? "#166534"
                                : String(
                                  exam.examStatus
                                )
                                  .toLowerCase() ===
                                  "active"
                                  ? "#1d4ed8"
                                  : "#92400e",
                          }}
                        >
                          {
                            exam.examStatus
                          }
                        </span>
                      </td>
                      {/* TOTAL FEES */}
                      <td>
                        <strong>
                          {formatFee(
                            exam
                              .examTotalFees
                          )}
                        </strong>
                      </td>
                      {/* PENDING */}
                      <td>
                        <strong
                          style={{
                            color:
                              Number(
                                exam
                                  .pendingFees
                              ) > 0
                                ? "#b45309"
                                : "#166534",
                          }}
                        >
                          {formatFee(
                            exam
                              .pendingFees
                          )}
                        </strong>
                      </td>
                      {/* PAYMENT STATUS */}
                      <td>
                        {exam
                          .paymentStatus ===
                          "Pending" ? (
                          <button
                            type="button"
                            onClick={() =>
                              openPaymentPopup(
                                exam
                              )
                            }
                            style={{
                              border:
                                "1px solid #f59e0b",
                              background:
                                "#fef3c7",
                              color:
                                "#92400e",
                              padding:
                                "6px 14px",
                              borderRadius: 20,
                              fontSize: 11,
                              fontWeight: 700,
                              cursor:
                                "pointer",
                            }}
                          >
                            Pending
                          </button>
                        ) : (
                          <span
                            style={{
                              display:
                                "inline-block",
                              padding:
                                "6px 14px",
                              borderRadius: 20,
                              background:
                                "#dcfce7",
                              color:
                                "#166534",
                              border:
                                "1px solid #86efac",
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            Paid
                          </span>
                        )}
                      </td>
                      {/* ACTION */}
                      <td>
                        <div className="ra">
                          <button
                            type="button"
                            className="btn btn-p btn-sm"
                            title="View"
                            onClick={() =>
                              openView(
                                exam
                              )
                            }
                          >
                            <I
                              n="eye"
                              s={11}
                              c="#fff"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>
        {/* PAGINATION */}
        {!loading &&
          totalRecords > 0 && (
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent:
                  "space-between",
                gap: 16,
                padding:
                  "14px 16px",
                borderTop:
                  "1px solid var(--bd)",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color:
                    "var(--t3)",
                }}
              >
                Showing{" "}
                <strong>
                  {
                    startIndex +
                    1
                  }
                </strong>
                {" - "}
                <strong>
                  {Math.min(
                    endIndex,
                    totalRecords
                  )}
                </strong>
                {" of "}
                <strong>
                  {
                    totalRecords
                  }
                </strong>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems:
                    "center",
                  gap: 5,
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  className="btn btn-g btn-sm"
                  disabled={
                    currentPage ===
                    1
                  }
                  onClick={() =>
                    setCurrentPage(
                      (previous) =>
                        Math.max(
                          1,
                          previous -
                          1
                        )
                    )
                  }
                >
                  ‹ Back
                </button>
                {pageNumbers.map(
                  (page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() =>
                        setCurrentPage(
                          page
                        )
                      }
                      style={{
                        height: 32,
                        minWidth: 32,
                        border:
                          currentPage ===
                            page
                            ? "1px solid #2563eb"
                            : "1px solid #e2e8f0",
                        borderRadius: 6,
                        background:
                          currentPage ===
                            page
                            ? "#2563eb"
                            : "#fff",
                        color:
                          currentPage ===
                            page
                            ? "#fff"
                            : "#475569",
                        cursor:
                          "pointer",
                      }}
                    >
                      {page}
                    </button>
                  )
                )}
                <button
                  type="button"
                  className="btn btn-g btn-sm"
                  disabled={
                    currentPage >=
                    totalPages
                  }
                  onClick={() =>
                    setCurrentPage(
                      (previous) =>
                        Math.min(
                          totalPages,
                          previous +
                          1
                        )
                    )
                  }
                >
                  Next ›
                </button>
              </div>
            </div>
          )}
      </div>
      {/* PAYMENT POPUP */}
      {payExam &&
        typeof document !==
        "undefined" &&
        createPortal(
          <PaymentPopup
            exam={payExam}
            loading={
              paymentLoading
            }
            onClose={
              closePaymentPopup
            }
            onPay={
              handlePayment
            }
            formatDate={
              formatDate
            }
            formatFee={
              formatFee
            }
          />,
          document.body
        )}
    </>
  );
}
// ======================================================
// PAYMENT POPUP
// ======================================================
function PaymentPopup({
  exam,
  loading,
  onClose,
  onPay,
  formatDate,
  formatFee,
}) {
  return (
    <div
      onClick={(event) => {
        if (
          event.target ===
          event.currentTarget
        ) {
          onClose();
        }
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        display: "flex",
        justifyContent:
          "center",
        alignItems:
          "center",
        padding: 20,
        background:
          "rgba(15,23,42,0.60)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background:
            "#ffffff",
          borderRadius: 15,
          overflow:
            "hidden",
          boxShadow:
            "0 25px 80px rgba(0,0,0,0.28)",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "center",
            padding:
              "17px 18px",
            borderBottom:
              "1px solid #e8edf3",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              Exam Fee Payment
            </div>
            <div
              style={{
                marginTop: 3,
                fontSize: 11,
                color:
                  "#64748b",
              }}
            >
              Pay securely using Razorpay
            </div>
          </div>
          <button
            type="button"
            disabled={
              loading
            }
            onClick={
              onClose
            }
            style={{
              width: 34,
              height: 34,
              border:
                "1px solid #e2e8f0",
              borderRadius: 8,
              background:
                "#fff",
              fontSize: 20,
            }}
          >
            ×
          </button>
        </div>
        {/* BODY */}
        <div
          style={{
            padding: 18,
          }}
        >
          <div
            style={{
              marginBottom: 15,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color:
                  "#64748b",
              }}
            >
              Student
            </div>
            <strong>
              {
                exam
                  .studentFullName
              }
            </strong>
          </div>
          <div
            style={{
              background:
                "#f8fafc",
              border:
                "1px solid #e2e8f0",
              borderRadius: 10,
              padding: 15,
              marginBottom: 18,
            }}
          >
            <div
              style={{
                fontSize: 10,
                color:
                  "#64748b",
                fontWeight: 600,
              }}
            >
              EXAM
            </div>
            <div
              style={{
                marginTop: 5,
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              {
                exam.examName
              }
            </div>
            <div
              style={{
                marginTop: 8,
                display: "flex",
                justifyContent:
                  "space-between",
                fontSize: 12,
              }}
            >
              <span>
                Exam Date
              </span>
              <strong>
                {formatDate(
                  exam.examDate
                )}
              </strong>
            </div>
          </div>
          {/* TOTAL */}
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              marginBottom: 14,
            }}
          >
            <span>
              Total Fees
            </span>
            <strong>
              {formatFee(
                exam
                  .examTotalFees
              )}
            </strong>
          </div>
          {/* STATUS */}
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              marginBottom: 14,
            }}
          >
            <span>
              Payment Status
            </span>
            <span
              style={{
                padding:
                  "5px 12px",
                borderRadius: 20,
                background:
                  "#fef3c7",
                color:
                  "#92400e",
                fontWeight: 700,
              }}
            >
              Pending
            </span>
          </div>
          {/* PAY AMOUNT */}
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "center",
              paddingTop: 15,
              borderTop:
                "1px solid #e2e8f0",
            }}
          >
            <span
              style={{
                fontWeight: 600,
              }}
            >
              Amount to Pay
            </span>
            <strong
              style={{
                fontSize: 24,
                color:
                  "#b45309",
              }}
            >
              {formatFee(
                exam
                  .pendingFees
              )}
            </strong>
          </div>
        </div>
        {/* FOOTER */}
        <div
          style={{
            display: "flex",
            justifyContent:
              "flex-end",
            gap: 10,
            padding:
              "14px 18px",
            background:
              "#f8fafc",
            borderTop:
              "1px solid #e8edf3",
          }}
        >
          <button
            type="button"
            className="btn btn-g"
            disabled={
              loading
            }
            onClick={
              onClose
            }
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-p"
            disabled={
              loading
            }
            onClick={
              onPay
            }
          >
            {loading
              ? "Processing..."
              : `Pay ${formatFee(
                exam
                  .pendingFees
              )}`}
          </button>
        </div>
      </div>
    </div>
  );
}
